import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { HomeIcon, PlacesIcon, BabcockIcon, FlightsIcon, CalendarIcon, MoreIcon } from '@/components/Icons';
import { InstallPrompt } from '@/components/InstallPrompt';
import { ViewportDebug } from '@/components/ViewportDebug';
import { ConciergePill } from '@/components/ConciergePill';
import { ConciergeSheet } from '@/components/ConciergeSheet';

const TABS = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/places', label: 'Places', icon: PlacesIcon, end: false },
  { to: '/babcock/national', label: 'Babcock National', icon: BabcockIcon, end: false },
  { to: '/travel-details', label: 'Travel details', icon: FlightsIcon, end: false },
  // Calendar = Chip's flights calendar (green is-visitation-free days).
  // Community What's On stays on Home (and More), not this tab.
  { to: '/flights', label: 'Calendar', icon: CalendarIcon, end: false },
  { to: '/more', label: 'More', icon: MoreIcon, end: false },
];

export function Layout() {
  const shellRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const [conciergeOpen, setConciergeOpen] = useState(false);

  // The app scrolls .app-main rather than the document, so a route change
  // no longer resets the scroll position for free — without this you tap a
  // tab and land halfway down the new page. Browsers restore document
  // scroll automatically; they do not do it for an arbitrary inner element.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  // Two-line "Babcock National" makes the in-flow bar taller than the old
  // ~64px. Publish the real height so the concierge pill, install banner,
  // and page padding clear it instead of a fixed offset.
  useLayoutEffect(() => {
    const shell = shellRef.current;
    const nav = navRef.current;
    if (!shell || !nav) return;

    const apply = () => {
      const navHeight = Math.ceil(nav.getBoundingClientRect().height);
      if (navHeight > 0) shell.style.setProperty('--bottom-nav-height', `${navHeight}px`);
      const prompt = shell.querySelector<HTMLElement>('.install-prompt');
      if (prompt) {
        const promptHeight = Math.ceil(prompt.getBoundingClientRect().height);
        if (promptHeight > 0) shell.style.setProperty('--install-prompt-height', `${promptHeight}px`);
      } else {
        shell.style.setProperty('--install-prompt-height', '0px');
      }
    };

    let cancelled = false;
    const publish = () => {
      if (!cancelled) apply();
    };
    publish();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    ro?.observe(nav);
    const mo = new MutationObserver(() => {
      const prompt = shell.querySelector<HTMLElement>('.install-prompt');
      if (prompt) ro?.observe(prompt);
      publish();
    });
    mo.observe(shell, { childList: true });
    document.fonts?.ready.then(publish).catch(() => {});
    return () => {
      cancelled = true;
      ro?.disconnect();
      mo.disconnect();
    };
  }, []);

  return (
    <div className="app-shell" ref={shellRef}>
      <main className="app-main" ref={mainRef}>
        <Outlet />
      </main>
      <InstallPrompt />
      <ConciergePill hidden={conciergeOpen} onOpen={() => setConciergeOpen(true)} />
      <ConciergeSheet open={conciergeOpen} onClose={() => setConciergeOpen(false)} />
      <ViewportDebug />
      <nav className="bottom-nav" aria-label="Primary" ref={navRef}>
        {TABS.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} aria-label={label}>
            <span className="bottom-nav__icon-wrap">
              <Icon />
            </span>
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
